/** Copyright © 2024 Qcells. All rights reserved.
This software is proprietary and confidential. Unauthorized use,
duplication, or distribution of software is strictly prohibited.
*/
import { Service } from '@hems/service/src/api/Service';
import { apiWrapper, getApiVersionHeader } from '@hems/service/src/util/apiHelper';
import { StorageHelper } from '@hems/util';
import { DEVICE_CONNECTION_RESPONSE_CODE } from '@hems/util/src/constant/device';
import { LANGUAGE } from '@hems/util/src/constant/languages';
export default class CommonService extends Service {
    constructor(axios) {
        super(axios);
    }
    getStorageKey(code) {
        return `${code.grpCd}${code.exclude?.join('_')}`.split('').reduce((a, b) => {
            a = (a << 5) - a + b.charCodeAt(0);
            return a & a;
        }, 0);
    }
    /** 코드 목록 가져오기 */
    getCodesByGroupCode = async (codes) => {
        const notExistedCodes = [];
        const result = {};
        // Session Storage에 있으면 result에 추가, 없으면 notExistedCodes에 파라미터 추가
        codes.forEach((item) => {
            const hashCode = this.getStorageKey(item);
            if (StorageHelper.isExistCode(hashCode)) {
                result[item.grpCd] = StorageHelper.getCode(hashCode);
            }
            else {
                notExistedCodes.push(item);
            }
        });
        let resResult;
        if (notExistedCodes.length > 0) {
            // notExistedCodes에 포함된 것만 API 호출
            const { data } = await this.post(apiWrapper.managerApi('/managements/commons/codes'), { codes: notExistedCodes });
            resResult = data;
            for (const obj of notExistedCodes) {
                const hashCode = this.getStorageKey(obj);
                // notExistedCodes의 결과를 Session Storage에 저장
                if (resResult[obj.grpCd])
                    StorageHelper.setCode(hashCode, resResult[obj.grpCd]);
            }
        }
        return { ...result, ...resResult };
    };
    /** Site Gen Type 조회 */
    getGenType = async (siteId) => {
        const { data } = await this.get(apiWrapper.managerApi(`/devices/profiles/${siteId}/types`));
        return data;
    };
    /** 장비 연결 상태 조회 */
    isConnection = async (deviceId) => {
        const { data } = await this.get(apiWrapper.managerApi(`/devices/connections/${deviceId}/status-devices`), undefined, {
            headers: getApiVersionHeader(),
        });
        if (data.responseCd === DEVICE_CONNECTION_RESPONSE_CODE.ONLINE)
            return true;
        return false;
    };
    /** 서버 상태 조회 */
    healthCheck = async () => {
        try {
            const { data } = await this.get('/health-check');
            return data;
        }
        catch (error) {
            return false;
        }
    };
    /** Google Map API Key 조회 */
    getGoogleAPIKey = async () => {
        const { data } = await this.get(apiWrapper.managerApi('/managements/commons/google/api-keys'));
        return data;
    };
    /** 공지 사항 조회 */
    getNotice = async (langCd = LANGUAGE.EN) => {
        const { data } = await this.get(apiWrapper.managerApi('/managements/commons/notices'), { lang: langCd });
        return data;
    };
    /** Home Owner에 이메일 전송 (siteId, activation code 등 정보 전달) */
    sendOwnerEmail = async (siteId) => {
        const { data } = await this.post(apiWrapper.managerApi(`/managements/mails/homeowner-welcome/${siteId}`));
        return data;
    };
    /** 파트너 계정 조회(검색) */
    getPartnerAccounts = async (query) => {
        const { data } = await this.get(apiWrapper.managerApiV1('/partners/accounts'), { search: query });
        return data;
    };
    /** 사이트에 권한이 존재하는 파트너 계정 조회 */
    getAuthorizedPartnerAccountsForSite = async (siteId) => {
        const { data } = await this.get(apiWrapper.managerApiV1(`/sites/${siteId}/accounts`));
        return data;
    };
    /** 파트너 계정 수정 */
    putPartnerAccounts = async (siteId, accountIds) => {
        const { data } = await this.put(apiWrapper.managerApiV1(`/sites/${siteId}/accounts`), { accountIds });
        return data;
    };
    /** Installer ID가 Entra ID에 존재하는지 체크 */
    checkInstallerId = (installerId) => this.get(apiWrapper.managerApiV1(`/partners/${installerId}`));
}
export { CommonService };
